import type { TFunction } from 'i18next'
import { useState } from 'react'
import { Navigate } from 'react-router'
import { AmountCard, Button, Card, Sheet, Skeleton, SuccessBlock } from '@/ds'
import type { AmountLine } from '@/ds'
import { useStay } from '@/app/stay/StayProvider'
import { dayMonth, dayMonthLong, formatMoney } from '@/lib/format'
import { useAppNavigate, withQuery } from '@/lib/query-params'
import { asyncValue, useAsync } from '@/lib/useAsync'
import { fianzaService } from '@/services/fianza'
import { reservaService } from '@/services/reserva'
import type { CancelQuote, Stay } from '@/services/reserva'
import { useApiLang, useSave } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { BackLink, plainText } from '../Pagos/shared'
import { BOOKING_ROUTES, minus, useBookingStay, useLang, writeDraft } from './shared'

const HOME = '/'

/**
 * 8.5 · Cancelar la reserva (Reserva.dc.html screen=cancel, §5f). Primero cuánto se devuelve y por qué (E13), la
 * alternativa de cambiar fechas antes del botón destructivo, y confirmación en hoja (E14), como CancelPreviewView.tsx.
 * La regla con porcentaje y fecha, la fianza que se libera y el plazo de la devolución solo con mock (D36).
 */
export function Cancelar() {
  const stay = useBookingStay()
  const { search } = useAppNavigate()
  if (!stay.flags.allowCancel || stay.phase === 'cancelled' || stay.phase === 'after') {
    return <Navigate to={withQuery(stay.phase === 'cancelled' || stay.phase === 'after' ? BOOKING_ROUTES.detail : BOOKING_ROUTES.unavailable('cancel'), search)} replace />
  }
  return <CancelScreen stay={stay} />
}

function CancelScreen({ stay }: { stay: Stay }) {
  const { t, lang } = useLang()
  const apiLang = useApiLang()
  const { go } = useAppNavigate()
  const { reload: reloadStay } = useStay()
  const [quote, reload] = useAsync(`cancelar:${stay.guestCode}`, (signal) => reservaService.quoteCancel(stay, apiLang, signal))
  const [deposit] = useAsync(`cancelar-fianza:${stay.guestCode}`, (signal) => fianzaService.getDeposit(stay, apiLang, signal))
  const [options] = useAsync(`reserva-cambios:${stay.guestCode}:cancelar`, (signal) => reservaService.getChangeOptions(stay, apiLang, signal))
  const [confirm, setConfirm] = useState(false)
  const [conditions, setConditions] = useState(false)
  const [done, setDone] = useState(false)
  const { saving, error, run } = useSave()
  const q = asyncValue(quote)
  const d = asyncValue(deposit)
  const datesAllowed = asyncValue(options)?.options.some((o) => o.type === 'dates' && o.allowed) ?? false
  const refund = q?.refund ?? null
  const refundText = refund ? formatMoney(refund, lang) : ''

  const cancel = async () => {
    const ok = await run(async () => {
      await reservaService.cancel(stay, apiLang)
      return true
    })
    if (ok) {
      setConfirm(false)
      setDone(true)
    }
  }

  const changeDates = () => {
    writeDraft({ guestCode: stay.guestCode, type: 'dates', input: null, quote: null, reason: null })
    go(BOOKING_ROUTES.pick('dates'))
  }

  if (done) {
    return (
      <main className="page reserva">
        <SuccessBlock
          title={t('v2.legacy.preview.labels.modifyBookingView.resume.cancelBooking.description')}
          headingLevel={2}
          {...(refund && refund.amount > 0 ? { amount: refundText } : {})}
          action={
            <Button
              onClick={() => {
                go(HOME, { replace: true })
                reloadStay()
              }}
            >
              {t('v2.reserva.cancelar.volver')}
            </Button>
          }
        >
          {refund && refund.amount > 0 ? (q?.refundEtaDays ? t('v2.reserva.cancelar.hechaDevolucionPlazo', { amount: refundText, min: q.refundEtaDays.min, max: q.refundEtaDays.max }) : t('v2.reserva.cancelar.hechaDevolucion', { amount: refundText })) : t('v2.reserva.cancelar.hecha')}
        </SuccessBlock>
      </main>
    )
  }

  return (
    <main className="page reserva">
      <div className="pagos__head">
        <BackLink to={BOOKING_ROUTES.detail} />
        <h1>{t('v2.reserva.cancelar.titulo')}</h1>
        <p className="page__lead">{t('v2.reserva.cancelar.lead')}</p>
      </div>
      {quote.kind === 'loading' ? (
        <div aria-busy="true" className="pagos__stack">
          <Skeleton height={260} />
          <Skeleton height={140} />
        </div>
      ) : quote.kind === 'error' ? (
        <SectionError code={quote.code} onRetry={reload} />
      ) : q ? (
        <>
          <AmountCard total={refundText || formatMoney({ amount: 0, currency: stay.currency }, lang)} totalLabel={t('v2.reserva.cancelar.devolveriamos')} totalRowLabel={t('v2.reserva.cancelar.devolveriamos')} lines={cancelLines(q, d?.amount ?? null, lang, t)} buttonLabel={null} note={q.refundEtaDays && refund && refund.amount > 0 ? t('v2.reserva.cancelar.plazo', { min: q.refundEtaDays.min, max: q.refundEtaDays.max }) : undefined} />
          {q.conditions && (
            <button type="button" className="pagos__link-button" onClick={() => setConditions(true)}>
              {t('v2.reserva.cancelar.condiciones')}
            </button>
          )}
          {datesAllowed && (
            <Card highlighted className="reserva__alternative">
              <span className="reserva__eyebrow">{t('v2.reserva.cancelar.antes')}</span>
              <h2 className="reserva__alternative-title">{t('v2.reserva.cancelar.alternativaTitulo')}</h2>
              <p className="reserva__alternative-text">
                {q.changeDatesFreeUntil
                  ? q.penalty && q.penalty.amount > 0
                    ? t('v2.reserva.cancelar.alternativaGratis', { date: dayMonthLong(q.changeDatesFreeUntil, lang), amount: formatMoney(q.penalty, lang) })
                    : t('v2.reserva.cancelar.alternativaGratisSinPerdida', { date: dayMonthLong(q.changeDatesFreeUntil, lang) })
                  : t('v2.reserva.cancelar.alternativa')}
              </p>
              <button type="button" className="pagos__chevron-link reserva__alternative-link" onClick={changeDates}>
                {t('v2.reserva.cancelar.cambiarEnSuLugar')}
              </button>
            </Card>
          )}
          <div className="pagos__group">
            <Button variant="destructive" onClick={() => setConfirm(true)}>
              {refund && refund.amount > 0 ? t('v2.reserva.cancelar.boton', { amount: refundText }) : t('v2.reserva.cancelar.botonSinDevolucion')}
            </Button>
            <Button variant="secondary" onClick={() => go(BOOKING_ROUTES.detail)}>
              {t('v2.reserva.cancelar.noCancelar')}
            </Button>
          </div>
        </>
      ) : null}
      <Sheet
        open={confirm}
        destructive
        title={t('v2.legacy.preview.labels.modifyBookingView.alertDialog.cancelBooking.title')}
        confirmLabel={t('v2.legacy.preview.labels.modifyBookingView.alertDialog.cancelBooking.continueButton')}
        cancelLabel={t('v2.legacy.preview.labels.modifyBookingView.alertDialog.cancelBooking.cancelButton')}
        onConfirm={() => void cancel()}
        onCancel={() => setConfirm(false)}
        loading={saving}
      >
        {t('v2.legacy.preview.labels.modifyBookingView.alertDialog.cancelBooking.description')}
        {refund && refund.amount > 0 && ` ${t('v2.reserva.cancelar.hojaDevolucion', { amount: refundText })}`}
        {error && (
          <span className="pagos__error" role="alert">
            {error}
          </span>
        )}
      </Sheet>
      <Sheet open={conditions} title={t('v2.legacy.payment.Conditions')} confirmHidden onCancel={() => setConditions(false)}>
        <span className="pagos__pre">{plainText(q?.conditions ?? '')}</span>
      </Sheet>
    </main>
  )
}

function cancelLines(q: CancelQuote, depositAmount: CancelQuote['paid'], lang: string, t: TFunction): AmountLine[] {
  const lines: AmountLine[] = []
  if (q.paid) lines.push({ label: t('v2.reserva.cancelar.pagado'), amount: formatMoney(q.paid, lang) })
  if (q.penalty && q.penalty.amount > 0) {
    const label = q.penaltyRule ? t('v2.reserva.cancelar.politica', { percent: q.penaltyRule.refundPercent, date: dayMonth(q.penaltyRule.after, lang) }) : t('v2.legacy.preview.labels.modifyBookingView.cancelPreview.cancelationCosts')
    lines.push({ label, amount: minus(q.penalty, lang) })
  }
  // Solo si el backend dice que la fianza se libera entera (mock); la API actual no lo dice (D36).
  if (q.depositReleased && depositAmount && depositAmount.amount > 0) lines.push({ label: t('v2.reserva.cancelar.fianza'), amount: formatMoney(depositAmount, lang), tone: 'success' })
  return lines
}
